import React, { useState, useEffect, useRef } from 'react';
import { X, Search, Check, Loader } from 'lucide-react';
import productService, { Product } from '../../services/productService';

interface ModelSelectorProps {
    value: string;
    onChange: (value: string) => void;
    error?: string;
    disabled?: boolean;
}

export const ModelSelector: React.FC<ModelSelectorProps> = ({ value, onChange, error, disabled }) => {
    const [query, setQuery] = useState('');
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [inventoryType, setInventoryType] = useState<'novias' | 'maestro'>('novias');
    const wrapperRef = useRef<HTMLDivElement>(null);

    // Convert comma-separated string to array of codes
    const selectedCodes = value ? value.split(',').map(code => code.trim()).filter(Boolean) : [];

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const fetchProducts = async () => {
            // Only search if query is 2+ chars or if dropdown is open just to have initial list
            if (showDropdown && !disabled) {
                setLoading(true);
                try {
                    const response = await productService.getProducts({
                        limit: 20,
                        search: query,
                        type: inventoryType
                    });
                    setProducts(response.data);
                } catch (err) {
                    console.error('Error fetching products:', err);
                } finally {
                    setLoading(false);
                }
            }
        };

        // Debounce search
        const timeoutId = setTimeout(fetchProducts, 300);
        return () => clearTimeout(timeoutId);
    }, [query, showDropdown, disabled, inventoryType]);

    const handleSelect = (code: string) => {
        if (!code || disabled) return;

        // Check if already selected
        if (selectedCodes.includes(code)) return;

        const newCodes = [...selectedCodes, code];
        onChange(newCodes.join(', '));
        setQuery('');
        setShowDropdown(false);
    };

    const handleRemove = (codeToRemove: string) => {
        if (disabled) return;
        const newCodes = selectedCodes.filter(code => code !== codeToRemove);
        onChange(newCodes.join(', '));
    };

    return (
        <div className="relative" ref={wrapperRef}>
            <div className="flex justify-between items-center mb-1">
                <label className="block text-sm font-semibold text-gray-800">
                    Vestido *
                </label>

                {/* Inventory Type Toggle */}
                {!disabled && (
                    <div className="flex bg-gray-100 rounded-lg p-1 text-xs">
                        <button
                            type="button"
                            onClick={() => setInventoryType('novias')}
                            className={`px-3 py-1 rounded-md transition-all ${inventoryType === 'novias'
                                    ? 'bg-white text-blue-600 shadow-sm font-medium'
                                    : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            Novias
                        </button>
                        <button
                            type="button"
                            onClick={() => setInventoryType('maestro')}
                            className={`px-3 py-1 rounded-md transition-all ${inventoryType === 'maestro'
                                    ? 'bg-white text-blue-600 shadow-sm font-medium'
                                    : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            General
                        </button>
                    </div>
                )}
            </div>

            {/* Selected Tags */}
            <div className="flex flex-wrap gap-2 mb-2">
                {selectedCodes.map(code => (
                    <span
                        key={code}
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${disabled ? 'bg-gray-100 text-gray-500 border-gray-200' : 'bg-blue-100 text-blue-800 border-blue-200'}`}
                    >
                        {code}
                        {!disabled && (
                            <button
                                type="button"
                                onClick={() => handleRemove(code)}
                                className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-blue-200 text-blue-600 focus:outline-none"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        )}
                    </span>
                ))}
            </div>

            {/* Input Field */}
            <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className={`h-4 w-4 ${disabled ? 'text-gray-300' : 'text-gray-400'}`} />
                </div>
                <input
                    type="text"
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setShowDropdown(true);
                    }}
                    onFocus={() => !disabled && setShowDropdown(true)}
                    disabled={disabled}
                    className={`w-full pl-10 pr-4 py-2 bg-white border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 placeholder-gray-400 ${error ? 'border-red-300' : 'border-gray-300'} ${disabled ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''}`}
                    placeholder={disabled ? 'No editable' : `Buscar en inventario ${inventoryType === 'novias' ? 'novias' : 'general'}...`}
                />
                {loading && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <Loader className="h-4 w-4 text-gray-400 animate-spin" />
                    </div>
                )}
            </div>

            {/* Dropdown Results */}
            {showDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {products.length > 0 ? (
                        <ul className="py-1">
                            {products.map((product) => {
                                const isSelected = selectedCodes.includes(product.cod_producto || '');
                                // Skip rendering if product has no code
                                if (!product.cod_producto) return null;

                                return (
                                    <li
                                        key={product.id_producto}
                                        onClick={() => handleSelect(product.cod_producto!)}
                                        className={`px-4 py-2 text-sm cursor-pointer hover:bg-gray-50 flex items-center justify-between ${isSelected ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                                            }`}
                                    >
                                        <div className="flex flex-col">
                                            <span className="font-medium flex items-center gap-2">
                                                {product.cod_producto}
                                                {product.marca && <span className="text-xs text-gray-500 font-normal px-1.5 py-0.5 bg-gray-100 rounded">{product.marca}</span>}
                                            </span>
                                            <span className="text-xs text-gray-500 mt-0.5">
                                                {[
                                                    product.color && `Color: ${product.color}`,
                                                    product.talla && `Talla: ${product.talla}`
                                                ].filter(Boolean).join(' • ')}
                                            </span>
                                        </div>
                                        {isSelected && <Check className="w-4 h-4 text-blue-600" />}
                                    </li>
                                );
                            })}
                        </ul>
                    ) : (
                        <div className="px-4 py-3 text-sm text-gray-500 text-center">
                            {loading ? 'Buscando...' : 'No se encontraron productos'}
                        </div>
                    )}
                </div>
            )}

            {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
        </div>
    );
};
